import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone,
  MessageSquare,
  Calendar,
  Car,
  Sparkles,
  Wrench,
  Shield,
  CheckCircle2,
  MapPin,
  ArrowRight,
  Star,
  Instagram,
  Zap,
  Home as HomeIcon,
  Droplets,
  Plus,
} from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import StickyMobileBar from "@/components/StickyMobileBar";
import { BUSINESS, api } from "@/lib/config";

const VEHICLE_LABEL = { car: "Car", suv: "SUV", truck: "Truck" };
const SERVICE_ICON = {
  exterior_detail: Droplets,
  interior_detail: Sparkles,
  full_detail: Wrench,
  ceramic_coating: Shield,
  ceramic_3yr: Shield,
  ceramic_5yr: Shield,
  paint_correction: Zap,
};

// TikTok isn't in lucide — inline SVG so the socials row feels complete.
const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.36a8.16 8.16 0 0 0 4.77 1.52V6.43a4.85 4.85 0 0 1-1.84.36z" />
  </svg>
);
const FacebookIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em" {...props}>
    <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.78-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.77l-.44 2.89h-2.33v6.99A10 10 0 0 0 22 12z" />
  </svg>
);

/**
 * Rotating hero backdrop — cycles user's photos every 5s.
 */
function HeroBackdrop({ images }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!images?.length) return;
    const t = setInterval(() => setI((n) => (n + 1) % images.length), 5500);
    return () => clearInterval(t);
  }, [images]);
  if (!images?.length) return null;
  return (
    <div className="absolute inset-0 overflow-hidden">
      <AnimatePresence mode="sync">
        <motion.img
          key={images[i]}
          src={images[i]}
          alt=""
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </AnimatePresence>
      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/50 to-black" />
    </div>
  );
}

/**
 * Compact service card — full detail block with add-ons visible right there.
 * Reduced red — only the "Book" arrow + price accent uses brand color.
 */
function ServiceCard({ id, name, description, startingAt, includes, Icon }) {
  const priceMin = Math.min(...Object.values(startingAt || {})).toString();
  return (
    <Link
      to={`/book?service=${id}`}
      data-testid={`service-tap-${id}`}
      className="group block relative rounded-2xl border border-white/10 bg-zinc-950 p-5 md:p-6 hover:border-white/25 hover:bg-zinc-900/60 transition-all active:scale-[0.99]"
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="w-11 h-11 rounded-xl bg-white/5 text-white grid place-items-center shrink-0">
          <Icon size={20} />
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Starting at</div>
          <div className="font-heading text-2xl md:text-3xl text-white tabular-nums">${priceMin}</div>
        </div>
      </div>
      <h3 className="font-heading text-xl md:text-2xl text-white mb-1.5">{name}</h3>
      <p className="text-zinc-400 text-sm leading-relaxed mb-4">{description}</p>
      {includes?.length > 0 && (
        <ul className="space-y-1.5 mb-5">
          {includes.slice(0, 4).map((line) => (
            <li key={line} className="flex items-start gap-2 text-xs text-zinc-400">
              <CheckCircle2 size={12} className="text-otgg-red mt-0.5 shrink-0" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-1.5 text-otgg-red text-sm font-semibold">
        Book this <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
      </div>
    </Link>
  );
}

export default function LandingPage() {
  const [services, setServices] = useState([]);
  const [protection, setProtection] = useState([]);
  const [addOns, setAddOns] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => {
      setServices(r.data.services || []);
      setProtection(r.data.protection || []);
      setAddOns(r.data.add_ons || []);
    }).catch(() => {});
  }, []);

  const mainServices = services.filter((s) => s.id !== "maintenance_clean");

  return (
    <div data-testid="landing-page" className="bg-black text-zinc-200 min-h-screen overflow-x-hidden">
      <Nav />

      {/* ============ HERO — horizontal / tight so mobile users see everything ============ */}
      <section
        data-testid="hero"
        className="relative min-h-[78svh] md:min-h-[80svh] lg:min-h-[92vh] flex items-center overflow-hidden pt-20 md:pt-24 pb-10 md:pb-8"
      >
        <HeroBackdrop images={BUSINESS.galleryImages} />

        <div className="relative w-full max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 bg-black/40 backdrop-blur text-[10px] md:text-[11px] uppercase tracking-[0.22em] text-zinc-200 mb-4 md:mb-6"
            >
              <MapPin size={12} className="text-otgg-red" /> {BUSINESS.serviceArea}
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="font-heading text-[38px] xs:text-[42px] sm:text-6xl md:text-7xl lg:text-[84px] leading-[0.95] tracking-tighter font-light text-white"
            >
              Showroom shine.
              <br />
              <span className="text-otgg-red">Wherever</span> you park.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.35 }}
              className="mt-4 md:mt-6 text-zinc-300 text-sm md:text-lg max-w-xl leading-relaxed"
            >
              Mobile detailing brought to your driveway. Book in under a minute.
            </motion.p>

            {/* Primary CTAs — red on the book, neutral on call */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="mt-5 md:mt-8 grid grid-cols-2 gap-3 max-w-md"
            >
              <Link
                to="/book"
                data-testid="hero-book-btn"
                className="group inline-flex items-center justify-center gap-2 h-13 md:h-16 py-3.5 rounded-2xl bg-otgg-red text-white font-bold text-sm md:text-lg hover:bg-otgg-red transition-all active:scale-[0.97]"
              >
                <Calendar size={16} /> Book Now
              </Link>
              <a
                href={BUSINESS.phoneHref}
                data-testid="hero-call-btn"
                className="inline-flex items-center justify-center gap-2 h-13 md:h-16 py-3.5 rounded-2xl bg-white text-black font-bold text-sm md:text-lg hover:bg-zinc-100 transition-all active:scale-[0.97]"
              >
                <Phone size={16} /> Call Now
              </a>
            </motion.div>

            {/* Trust bar + socials */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="mt-5 md:mt-8 flex flex-wrap items-center gap-x-5 gap-y-3 text-sm"
            >
              <a
                href={BUSINESS.googleReviewUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-google-reviews"
                className="group inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 bg-black/40 backdrop-blur hover:border-white/40 transition-colors"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} size={12} className="fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <span className="text-white font-semibold text-xs">{BUSINESS.googleRating.toFixed(1)}</span>
                <span className="text-zinc-400 text-xs group-hover:text-white transition-colors">See reviews</span>
              </a>
              <div className="h-4 w-px bg-white/15" />
              <div className="flex items-center gap-3.5">
                <a href={BUSINESS.instagramUrl} target="_blank" rel="noreferrer" data-testid="hero-instagram" title="Instagram" className="text-zinc-300 hover:text-white transition-colors">
                  <Instagram size={16} />
                </a>
                <a href={BUSINESS.facebookUrl} target="_blank" rel="noreferrer" data-testid="hero-facebook" title="Facebook" className="text-zinc-300 hover:text-white transition-colors">
                  <FacebookIcon width={16} height={16} />
                </a>
                <a href={BUSINESS.tiktokUrl} target="_blank" rel="noreferrer" data-testid="hero-tiktok" title="TikTok" className="text-zinc-300 hover:text-white transition-colors">
                  <TikTokIcon width={16} height={16} />
                </a>
                <span className="text-zinc-500 text-xs">@{BUSINESS.instagram}</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ SERVICES — full detail + add-ons all grouped here ============ */}
      <section id="services" data-testid="services-section" className="py-14 md:py-24 border-t border-zinc-900">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="mb-8 md:mb-12">
            <div className="text-xs uppercase tracking-[0.25em] text-otgg-red mb-2">Services</div>
            <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl tracking-tighter font-light text-white">
              Pick your detail.
            </h2>
            <p className="text-zinc-400 mt-3 max-w-lg text-sm md:text-base">
              Clear starting prices per vehicle. Add-ons below for the messy jobs — quoted upfront, never a surprise.
            </p>
          </div>

          {/* 3 service cards */}
          <div className="grid md:grid-cols-3 gap-3 md:gap-5 mb-8 md:mb-10">
            {mainServices.map((s) => (
              <ServiceCard
                key={s.id}
                id={s.id}
                name={s.name}
                description={s.description}
                startingAt={s.starting_at}
                includes={s.includes}
                Icon={SERVICE_ICON[s.id] || Car}
              />
            ))}
          </div>

          {/* Add-ons — inline, right with the services so no guessing */}
          {addOns.length > 0 && (
            <div data-testid="addons-inline" className="rounded-2xl border border-zinc-900 bg-zinc-950/60 p-5 md:p-7">
              <div className="flex items-center justify-between mb-4 md:mb-5">
                <div className="flex items-center gap-2">
                  <Plus size={16} className="text-otgg-red" />
                  <div>
                    <div className="font-heading text-lg md:text-xl text-white">Add-ons</div>
                    <div className="text-xs text-zinc-500">Optional — for extra dirty jobs</div>
                  </div>
                </div>
                <Link to="/book" className="hidden md:inline-flex items-center gap-1.5 text-otgg-red text-sm font-semibold hover:gap-2 transition-all">
                  Add during booking <ArrowRight size={13} />
                </Link>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {addOns.map((a) => (
                  <div
                    key={a.id}
                    data-testid={`addon-item-${a.id}`}
                    className="flex items-center justify-between rounded-xl border border-zinc-900 bg-black/50 px-4 py-3"
                  >
                    <span className="text-zinc-200 text-sm">{a.name}</span>
                    <span className="text-white font-heading text-base tabular-nums">+${a.price_from}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ============ PRICING TABLE ============ */}
      <section id="pricing" data-testid="pricing-section" className="py-14 md:py-24 border-t border-zinc-900 bg-zinc-950/40">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <div className="text-center mb-8 md:mb-12">
            <div className="text-xs uppercase tracking-[0.25em] text-otgg-red mb-2">Starting Prices</div>
            <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl tracking-tighter font-light text-white">
              Simple. Honest.
            </h2>
          </div>

          <div className="rounded-2xl border border-zinc-800 bg-black overflow-hidden">
            <div className="grid grid-cols-4 text-[10px] md:text-xs uppercase tracking-[0.18em] text-zinc-500 border-b border-zinc-900 bg-zinc-950">
              <div className="py-3 md:py-4 px-3 md:px-6">Service</div>
              <div className="py-3 md:py-4 px-2 md:px-4 text-center">Car</div>
              <div className="py-3 md:py-4 px-2 md:px-4 text-center">SUV</div>
              <div className="py-3 md:py-4 px-2 md:px-4 text-center">Truck</div>
            </div>
            {mainServices.map((s) => (
              <Link
                key={s.id}
                to={`/book?service=${s.id}`}
                data-testid={`pricing-row-${s.id}`}
                className="grid grid-cols-4 items-center border-b border-zinc-900 last:border-b-0 hover:bg-zinc-950/60 transition-colors active:bg-zinc-900"
              >
                <div className="py-4 md:py-5 px-3 md:px-6">
                  <div className="text-white font-medium text-sm md:text-base">{s.name}</div>
                  <div className="text-zinc-500 text-[10px] md:text-xs mt-0.5">Starting at</div>
                </div>
                {["car", "suv", "truck"].map((v) => (
                  <div key={v} className="py-4 md:py-5 px-2 md:px-4 text-center">
                    <span className="font-heading text-lg md:text-2xl text-white tabular-nums">${s.starting_at?.[v]}</span>
                  </div>
                ))}
              </Link>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/book"
              data-testid="pricing-book-btn"
              className="inline-flex items-center gap-2 h-14 px-8 rounded-full bg-otgg-red text-white font-bold text-base hover:bg-otgg-red transition-all active:scale-[0.97]"
            >
              <Calendar size={16} /> Book Your Detail
            </Link>
          </div>
        </div>
      </section>

      {/* ============ PROTECTION — 3yr + 5yr ceramic side-by-side ============ */}
      {protection.length > 0 && (
        <section id="protection" data-testid="protection-section" className="py-14 md:py-24 border-t border-zinc-900">
          <div className="max-w-6xl mx-auto px-5 md:px-10">
            <div className="text-center mb-8 md:mb-12">
              <div className="text-xs uppercase tracking-[0.25em] text-otgg-red mb-2 flex items-center justify-center gap-2">
                <Shield size={12} /> Paint Protection
              </div>
              <h2 className="font-heading text-3xl md:text-5xl tracking-tighter font-light text-white">Lock in the gloss.</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-4 md:gap-5">
              {protection.map((p) => {
                const Icon = SERVICE_ICON[p.id] || Shield;
                const featured = p.id === "ceramic_5yr";
                const priceMin = Math.min(...Object.values(p.starting_at || {}));
                return (
                  <Link
                    key={p.id}
                    to={`/book?service=${p.id}`}
                    data-testid={`protection-card-${p.id}`}
                    className={`group relative rounded-2xl border p-5 md:p-6 flex flex-col active:scale-[0.99] transition-all ${
                      featured
                        ? "border-otgg-red/60 bg-zinc-950 hover:border-otgg-red"
                        : "border-zinc-900 bg-zinc-950 hover:border-zinc-700"
                    }`}
                  >
                    {featured && (
                      <div className="absolute -top-3 left-5 px-2.5 py-1 rounded-full bg-otgg-red text-white text-[10px] uppercase tracking-[0.2em] font-bold">
                        Best Protection
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-3">
                      <Icon className="text-white" size={22} />
                      {p.duration_years && (
                        <div className="text-right">
                          <div className="font-heading text-2xl md:text-3xl text-white leading-none tabular-nums">{p.duration_years}</div>
                          <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">year coat</div>
                        </div>
                      )}
                    </div>
                    <h3 className="font-heading text-lg md:text-xl text-white mb-2">{p.name}</h3>
                    <p className="text-zinc-400 text-sm leading-relaxed mb-4">{p.description}</p>
                    <div className="flex items-baseline gap-2 mb-4 mt-auto">
                      <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Starting at</span>
                      <span className="font-heading text-2xl text-white tabular-nums">${priceMin}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-otgg-red text-sm font-semibold">
                      Book this <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ============ WHY OTGG · quick trust bullets ============ */}
      <section data-testid="why-strip" className="py-14 md:py-20 border-t border-zinc-900">
        <div className="max-w-6xl mx-auto px-5 md:px-10 grid md:grid-cols-3 gap-6 md:gap-10">
          {[
            { Icon: HomeIcon, title: "We come to you", body: "Home, work, or jobsite. Fully self-contained — our own water and power." },
            { Icon: Sparkles, title: "Detailed by hand", body: "Foam pre-soak, two-bucket wash, and hand-finished microfiber. No swirls." },
            { Icon: CheckCircle2, title: "Honest pricing", body: "Clear starting prices per vehicle. Add-ons quoted before we ever start." },
          ].map(({ Icon, title, body }) => (
            <div key={title} className="flex gap-4 items-start">
              <div className="w-10 h-10 rounded-xl bg-white/5 text-white grid place-items-center shrink-0">
                <Icon size={18} />
              </div>
              <div>
                <div className="font-heading text-lg text-white mb-1">{title}</div>
                <p className="text-zinc-400 text-sm leading-relaxed">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section data-testid="final-cta" className="py-16 md:py-24 border-t border-zinc-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={BUSINESS.heroImage} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black" />
        </div>
        <div className="relative max-w-3xl mx-auto px-5 md:px-10 text-center">
          <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl tracking-tighter font-light text-white mb-4 md:mb-5">
            Ready when <span className="text-otgg-red">you are.</span>
          </h2>
          <p className="text-zinc-400 max-w-lg mx-auto mb-6 md:mb-8 text-sm md:text-base leading-relaxed">
            Under a minute to book. We confirm by call or text and show up ready to work.
          </p>
          <div className="grid grid-cols-2 gap-3 max-w-md mx-auto">
            <Link
              to="/book"
              data-testid="final-book-btn"
              className="inline-flex items-center justify-center gap-2 h-14 md:h-16 rounded-2xl bg-otgg-red text-white font-bold text-base md:text-lg hover:bg-otgg-red transition-all active:scale-[0.97]"
            >
              <Calendar size={18} /> Book Now
            </Link>
            <a
              href={BUSINESS.smsHref}
              data-testid="final-text-btn"
              className="inline-flex items-center justify-center gap-2 h-14 md:h-16 rounded-2xl bg-white text-black font-bold text-base md:text-lg hover:bg-zinc-100 transition-all active:scale-[0.97]"
            >
              <MessageSquare size={18} /> Text Us
            </a>
          </div>

          {/* Social row */}
          <div className="mt-8 md:mt-10 flex items-center justify-center gap-5 text-zinc-400">
            <a href={BUSINESS.instagramUrl} target="_blank" rel="noreferrer" data-testid="final-instagram" className="hover:text-white transition-colors flex items-center gap-1.5 text-sm">
              <Instagram size={16} /> Instagram
            </a>
            <a href={BUSINESS.facebookUrl} target="_blank" rel="noreferrer" data-testid="final-facebook" className="hover:text-white transition-colors flex items-center gap-1.5 text-sm">
              <FacebookIcon width={16} height={16} /> Facebook
            </a>
            <a href={BUSINESS.tiktokUrl} target="_blank" rel="noreferrer" data-testid="final-tiktok" className="hover:text-white transition-colors flex items-center gap-1.5 text-sm">
              <TikTokIcon width={16} height={16} /> TikTok
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <StickyMobileBar />
    </div>
  );
}
